import Button from "@mui/material/Button";
import React, { useContext, useEffect, useState } from "react";
import api from "../../Services/api";
import { errorManagement } from "../../Services/errorManagement";
import GenericDialog from "../GenericDialog";
import { HomeAppContext } from "../Home";
import Form, { DatasetFormMode } from "./DatasetForm";

export default function Edit({ open, setOpen, entry, onEdit }) {
  const { homeDispatch } = useContext(HomeAppContext);
  const [internalEntry, setInternalEntry] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saveDisabled, setSaveDisabled] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    if (entry !== null && open) {
      const clonedEntry = JSON.parse(JSON.stringify(entry));
      
      if (!clonedEntry.variables) {
        clonedEntry.variables = [];
      }
      
      if (!clonedEntry.dataMappings) {
        clonedEntry.dataMappings = [];
      }
      
      if (!clonedEntry.fileIds) {
        if (clonedEntry.files && clonedEntry.files.length > 0) {
          clonedEntry.fileIds = clonedEntry.files.map(file => file.fileId);
        } else {
          clonedEntry.fileIds = [];
        }
      }
      
      setInternalEntry(clonedEntry);
      setErrors([]);
      setLoading(false);
      setSubmitted(false);
    } else {
      setInternalEntry(null);
    }
  }, [entry, open]);

  const prepareDataForSubmission = () => {
    if (!internalEntry) return null;

    const fileIds = Array.isArray(internalEntry.fileIds) 
      ? internalEntry.fileIds.map(file => {
          if (typeof file === 'object' && file !== null) {
            return file.id || file.fileId || 0;
          }
          return typeof file === 'number' ? file : 0;
        }).filter(id => id > 0)
      : [];

    const values = Array.isArray(internalEntry.values)
      ? internalEntry.values.map(value => {
          if (typeof value === 'string') {
            return value.split('`');
          }
          return Array.isArray(value) ? value : [];
        })
      : [];

    const variables = Array.isArray(internalEntry.variables) 
      ? [...internalEntry.variables]
      : [];

      const validMappings = (internalEntry.dataMappings || [])
      .filter(mapping => mapping.variableName && mapping.variableName.trim() !== '')
      .map(mapping => ({
        ...mapping,
        type: typeof mapping.type === 'number' ? mapping.type : parseInt(mapping.type, 10)
      }));

    const result = {
      id: internalEntry.id,
      name: internalEntry.name || "",
      description: internalEntry.description || "",
      fileIds: fileIds,
      variables: variables,
      values: values,
      dataMappings: validMappings.length > 0 ? validMappings : null
    };
    
    return result;
  };

  const handleClose = () => {
    setOpen(false);
    setLoading(false);
  };

  const handleCloseSave = () => {
    if (!internalEntry || !internalEntry.id) {
      homeDispatch({
        type: "NOTIFICATION",
        data: { message: "Error: Missing dataset ID", type: "error" }
      });
      return;
    }

    const updateDataset = prepareDataForSubmission();
    if (!updateDataset) {
      homeDispatch({
        type: "NOTIFICATION",
        data: { message: "Error: Failed to prepare dataset data", type: "error" }
      });
      return;
    }

    homeDispatch({
      type: "NOTIFICATION",
      data: { message: "Updating " + internalEntry.name, loading: true }
    });
    setSubmitted(true);
    setLoading(true);
    
    api
      .put(`/api/datasets/${internalEntry.id}`, updateDataset)
      .then((response) => {
        homeDispatch({
          type: "NOTIFICATION",
          data: {
            message: internalEntry.name + " updated successfully",
            type: "success"
          }
        });
        setOpen(false);
        setLoading(false);
        onEdit();
      })
      .catch((error) => {
        setLoading(false);
        errorManagement.formErrors(error, homeDispatch, setErrors);
      });
  };
  
  const actions = (
    <>
      <Button onClick={handleClose} disabled={loading}>
        Cancel
      </Button>
      <Button
        onClick={handleCloseSave}
        color="primary"
        variant="outlined"
        autoFocus
        disabled={loading || saveDisabled}
      >
        Save Dataset
      </Button>
    </>
  );
  
  return internalEntry === null ? null : (
    <GenericDialog
      maxWidth={"sm"}
      open={open}
      onClose={handleClose}
      title={"Edit Dataset"}
      actions={actions}
    >
      <Form
        mode={DatasetFormMode.EDIT}
        entity={internalEntry}
        open={open}
        errors={errors}
        setEntity={setInternalEntry}
        loading={loading}
        submitted={submitted}
        setSaveDisabled={setSaveDisabled}
      />
    </GenericDialog>
  );
}
